/**
 * Amplify API Wrapper with Auth Error Handling for Monutrack
 *
 * This module wraps AWS Amplify's API methods (get, post, put, del, patch) with:
 * 1. Automatic tenant domain injection (when available)
 * 2. Authentication error detection and handling
 * 3. Support for multi-tenant architecture
 *
 * Usage: Replace imports from 'aws-amplify/api' with this wrapper
 *
 * Before:
 *   import { get, post, put, del } from 'aws-amplify/api'
 *
 * After:
 *   import { get, post, put, del } from '@/api/lib/amplify-wrapper'
 *
 * The wrapper will automatically:
 * - Inject X-Tenant-Domain header when tenant context is available
 * - Detect NotAuthorizedException errors
 * - Sign out the user
 * - Redirect to login page
 * - Preserve return URL for post-login redirect
 */

import {
  get as amplifyGet,
  post as amplifyPost,
  put as amplifyPut,
  patch as amplifyPatch,
  del as amplifyDel,
} from 'aws-amplify/api'
import { isAuthError, handleAuthError, attemptTokenRefresh } from './error-handler'

// JWT token storage for signing sessions using localStorage
// This is separate from Amplify auth and only used for OTP-verified signing sessions
// Stored in localStorage so it persists across page refreshes in the same browser

const JWT_STORAGE_KEY = 'monumenta_session_jwt'

export function setGlobalJWT(token: string | null) {
  console.log('🔑 [API Client] JWT updated:', token ? 'SET' : 'CLEARED')

  if (token) {
    // Store JWT in localStorage
    try {
      localStorage.setItem(JWT_STORAGE_KEY, token)
      console.log('✅ [API Client] JWT stored in localStorage')
    } catch (error) {
      console.error('❌ [API Client] Failed to store JWT in localStorage:', error)
      // Fall back to memory storage if localStorage fails
    }
  } else {
    // Clear JWT from localStorage
    try {
      localStorage.removeItem(JWT_STORAGE_KEY)
      console.log('✅ [API Client] JWT removed from localStorage')
    } catch (error) {
      console.error('❌ [API Client] Failed to remove JWT from localStorage:', error)
    }
  }
}

export function getGlobalJWT(): string | null {
  try {
    const token = localStorage.getItem(JWT_STORAGE_KEY)
    if (!token) {
      return null
    }

    // Validate JWT hasn't expired
    try {
      // Decode JWT payload (without verifying signature - server will verify)
      const parts = token.split('.')
      if (parts.length !== 3) {
        console.warn('⚠️ [API Client] Invalid JWT format, clearing')
        clearGlobalJWT()
        return null
      }

      const payload = JSON.parse(atob(parts[1]))
      const exp = payload.exp

      if (exp) {
        const now = Math.floor(Date.now() / 1000)
        if (exp < now) {
          console.warn('⚠️ [API Client] JWT expired, clearing')
          clearGlobalJWT()
          return null
        }
      }

      console.log('🔑 [API Client] Valid JWT retrieved from localStorage')
      return token
    } catch (decodeError) {
      console.error('❌ [API Client] Failed to decode JWT, clearing:', decodeError)
      clearGlobalJWT()
      return null
    }
  } catch (error) {
    console.error('❌ [API Client] Failed to retrieve JWT from localStorage:', error)
    return null
  }
}

export function clearGlobalJWT() {
  console.log('🔑 [API Client] Clearing JWT')
  setGlobalJWT(null)
}

/**
 * Get tenant domain for API calls.
 *
 * Resolution order:
 *   1. If hostname is `monutrack.dev` / `monutrack.app` OR any subdomain
 *      of them, use the hostname directly — every tenant lives at
 *      `<sub>.monutrack.{dev,app}`, so the subdomain IS the tenant identity.
 *   2. Otherwise (localhost, Vercel preview URLs, custom hostnames, SSR),
 *      fall back to `VITE_TENANT_DOMAIN`.
 *   3. If no env var is set, default to `monutrack.app`.
 *
 * Kept in sync with `api-utils.ts` and `tenant-service.ts`. Originally three
 * copies were needed because Amplify get/post/put/del all flow through this
 * wrapper independently of the fetch-based services.
 */
function getTenantDomain(): string | undefined {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    if (hostname === 'monutrack.dev' || hostname.endsWith('.monutrack.dev')) return hostname
    if (hostname === 'monutrack.app' || hostname.endsWith('.monutrack.app')) return hostname
  }

  // Unknown hostname / localhost / SSR: env var, then default
  const envTenantDomain = import.meta.env.VITE_TENANT_DOMAIN
  if (envTenantDomain) {
    return envTenantDomain
  }

  return 'monutrack.app'
}

/**
 * Inject tenant domain header into API request input
 *
 * Automatically reads tenantDomain from URL/context and injects it
 * as X-Tenant-Domain header when available.
 *
 * @param input - Amplify API input (get/post/put/patch/del)
 * @returns Input with X-Tenant-Domain header injected (if tenant domain is available)
 */
function injectTenantHeader<T extends Parameters<typeof amplifyGet>[0] | Parameters<typeof amplifyPost>[0] | Parameters<typeof amplifyPut>[0] | Parameters<typeof amplifyPatch>[0] | Parameters<typeof amplifyDel>[0]>(
  input: T
): T {
  const tenantDomain = getTenantDomain()

  const headers: Record<string, string> = {
    ...input.options?.headers,
  }

  // Only inject tenant header if we have a tenant domain
  if (tenantDomain) {
    headers['X-Tenant-Domain'] = tenantDomain
    console.log('🔧 [API Client] Injecting tenant domain header:', tenantDomain)
  } else {
    console.log('🔧 [API Client] No tenant domain detected, skipping tenant header injection')
  }

  // Check if authMode is set in options (type-unsafe access for extensibility)
  const existingAuthMode = (input.options as any)?.authMode
  console.log('🔧 [API Client] Final headers:', headers)
  console.log('🔧 [API Client] Auth mode:', existingAuthMode || 'default (userPool with fallback to token)')

  const options: any = {
    ...input.options,
    headers,
    // Preserve authMode if explicitly set, otherwise use 'userPool'
    // Note: For OIDC-authenticated users (e.g., Wrike OAuth), Amplify will automatically
    // fall back to using the access token as a bearer token if IAM credentials aren't available
    ...(existingAuthMode && { authMode: existingAuthMode }),
  }

  return {
    ...input,
    options,
  } as T
}

/**
 * Wrap an Amplify API call with auth error handling and tenant injection
 * Returns the Operation directly but with error handling applied
 *
 * For 401 errors, attempts token refresh and retries the request once
 */
function wrapApiCall<T>(
  apiCall: () => T,
  retryApiCall?: () => T
): T {
  const operation = apiCall()

  // Create a proxy to intercept the response access
  const wrappedOperation = {
    ...operation,
    response: (async () => {
      try {
        return await (operation as any).response
      } catch (error) {
        // 🔍 DEBUG: Log the raw Amplify error to understand its structure
        console.error('🔍 [Amplify Wrapper] Raw error caught:', {
          errorType: error?.constructor?.name,
          errorMessage: (error as any)?.message,
          errorResponse: (error as any)?.response,
          errorStatus: (error as any)?.response?.statusCode || (error as any)?.status || (error as any)?.statusCode,
          fullError: error,
        })

        const status = (error as any)?.response?.statusCode || (error as any)?.status || (error as any)?.statusCode

        // 🔄 For 401 errors, attempt token refresh and retry ONCE
        if (status === 401 && retryApiCall) {
          console.log('🔄 [Amplify Wrapper] 401 detected, attempting token refresh...')
          const refreshed = await attemptTokenRefresh()

          if (refreshed) {
            console.log('🔄 [Amplify Wrapper] Token refresh succeeded, retrying request...')
            try {
              // Create a new operation with fresh tokens
              const retryOperation = retryApiCall()
              return await (retryOperation as any).response
            } catch (retryError) {
              console.error('🔄 [Amplify Wrapper] Retry after token refresh failed:', retryError)
              // Don't try to refresh again, just handle the auth error
              const domain = getTenantDomain()
              await handleAuthError(retryError, domain, true) // skipRefresh = true
              throw retryError
            }
          } else {
            console.log('🔄 [Amplify Wrapper] Token refresh failed, proceeding to logout')
          }
        }

        // Check if this is an auth error
        if (isAuthError(error)) {
          const domain = getTenantDomain()
          await handleAuthError(error, domain, status === 401) // Skip refresh if we already tried
          throw error // Won't execute due to redirect
        }

        // Re-throw non-auth errors
        throw error
      }
    })(),
  }

  return wrappedOperation as T
}

/**
 * GET request with automatic tenant injection and auth error handling
 *
 * @param input - Amplify GET input (X-Tenant-Domain will be auto-injected if available)
 *
 * @example
 * ```typescript
 * // Before: Had to pass tenantDomain manually (if needed)
 * const { response } = await get({
 *   apiName: 'monumenta',
 *   path: '/employees',
 *   options: { headers: { 'X-Tenant-Domain': tenantDomain } }
 * })
 *
 * // After: Tenant domain auto-injected when available!
 * const { response } = await get({
 *   apiName: 'monumenta',
 *   path: '/employees'
 * })
 * ```
 */
export function get(input: Parameters<typeof amplifyGet>[0]): ReturnType<typeof amplifyGet> {
  const inputWithTenant = injectTenantHeader(input)
  // Pass a retry callback that creates a fresh request with potentially refreshed tokens
  return wrapApiCall(
    () => amplifyGet(inputWithTenant),
    () => amplifyGet(injectTenantHeader(input)) // Re-inject headers with fresh state
  )
}

/**
 * POST request with automatic tenant injection and auth error handling
 *
 * @param input - Amplify POST input (X-Tenant-Domain will be auto-injected if available)
 */
export function post(input: Parameters<typeof amplifyPost>[0]): ReturnType<typeof amplifyPost> {
  const inputWithTenant = injectTenantHeader(input)
  return wrapApiCall(
    () => amplifyPost(inputWithTenant),
    () => amplifyPost(injectTenantHeader(input))
  )
}

/**
 * PUT request with automatic tenant injection and auth error handling
 *
 * @param input - Amplify PUT input (X-Tenant-Domain will be auto-injected if available)
 */
export function put(input: Parameters<typeof amplifyPut>[0]): ReturnType<typeof amplifyPut> {
  const inputWithTenant = injectTenantHeader(input)
  return wrapApiCall(
    () => amplifyPut(inputWithTenant),
    () => amplifyPut(injectTenantHeader(input))
  )
}

/**
 * PATCH request with automatic tenant injection and auth error handling
 *
 * @param input - Amplify PATCH input (X-Tenant-Domain will be auto-injected if available)
 */
export function patch(input: Parameters<typeof amplifyPatch>[0]): ReturnType<typeof amplifyPatch> {
  const inputWithTenant = injectTenantHeader(input)
  return wrapApiCall(
    () => amplifyPatch(inputWithTenant),
    () => amplifyPatch(injectTenantHeader(input))
  )
}

/**
 * DELETE request with automatic tenant injection and auth error handling
 *
 * @param input - Amplify DELETE input (X-Tenant-Domain will be auto-injected if available)
 */
export function del(input: Parameters<typeof amplifyDel>[0]): ReturnType<typeof amplifyDel> {
  const inputWithTenant = injectTenantHeader(input)
  return wrapApiCall(
    () => amplifyDel(inputWithTenant),
    () => amplifyDel(injectTenantHeader(input))
  )
}
